import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import CaptchaBox from '../components/auth/CaptchaBox';
import Field from '../components/ui/Field';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { login as loginApi } from '../api/resources';

export default function Login() {
  const { login } = useAuth();
  const { success, error } = useToast();
  const nav = useNavigate();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [captcha, setCaptcha] = useState(null);
  const [captchaErr, setCaptchaErr] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setCaptchaErr('');
    if (!name || !password) return setErr('Please enter your name and password.');
    setBusy(true);
    try {
      const data = await loginApi({ name, password, challengeId: captcha?.challengeId, captchaCode: captcha?.code });
      login(data.token, data.user);
      success('Login successful! Redirecting…');
      // Worker logins have their own portal; everyone else lands on the dashboard.
      const home = data.user?.companies?.[0]?.role === 'WORKER' ? '/portal' : '/dashboard';
      setTimeout(() => nav(home), 700);
    } catch (x) {
      const msg = x.response?.data?.message || 'Login failed. Please try again.';
      if (msg.toLowerCase().includes('security')) setCaptchaErr(msg);
      else setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="font-display text-2xl font-semibold text-brand-900">Welcome back</h2>
      <p className="mt-1 text-sm text-content-muted">Sign in to your enterprise resource hub.</p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field label="Name" required error={err}>
          <input className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>

        <Field label="Password" required>
          <div className="relative">
            <input
              className="input pr-12"
              type={showPw ? 'text' : 'password'}
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted">
              {showPw ? '🙈' : '👁'}
            </button>
          </div>
        </Field>

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-content-muted">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-500" />
            Remember me
          </label>
          <button type="button" onClick={() => error('Password reset is handled by your administrator.')} className="font-medium text-brand-700 hover:underline">
            Forgot password?
          </button>
        </div>

        <CaptchaBox onCaptcha={setCaptcha} error={captchaErr} />

        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'Signing in…' : 'Log In to ERP'}
        </button>

        <p className="text-center text-sm text-content-muted">
          Don’t have an account?{' '}
          <Link to="/register" className="font-semibold text-brand-700 hover:underline">
            Create Account
          </Link>
        </p>
        <p className="text-center text-xs text-content-subtle">
          Need help? <span className="text-brand-600">Contact support</span>
        </p>
      </form>
    </AuthLayout>
  );
}